import { toast } from 'react-toastify'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { Paper, Slide, useTheme } from '@material-ui/core'

import { Container } from '../../../components'
import { ConfigureInspectionType } from './ConfigureInspectionType'
import { AreaOrder, IdBoolMap, InspectionTypeCategory } from '../../../models'
import { useAppDispatch, useAptConfig, useUser } from '../../../hooks'
import {
    CreateInspectionTypeRequest,
    EditInspectionTypeRequest,
    createInspectionType,
    editInspectionTypeReq,
    getStatusGroupList,
    getStatusList,
} from '../../../store'
import { PreviewInspectionType } from './PreviewInspectionType'
import { hasPermission } from '../../../models/Users/services'
import { INSPECTION_PREVIEW_EDITOR } from '../../../models'
import { Routes } from '../../../navigation/config/routes'

export const CONGIFURE_INSPECTION_TYPE_STEP = 0
export const PREVIEW_INSPECTION_TYPE_STEP = 1

export const InspectionConfigurationPage = () => {
    const { id } = useParams<{ id?: string }>()
    const history = useHistory()
    const location = useLocation()
    const theme = useTheme()
    const dispatch = useAppDispatch()

    // Parse query params to get initial tab
    const queryParams = new URLSearchParams(location.search)
    const initialTabParam = queryParams.get('tab')

    const {
        inspectionTypeList,
        inventoryConfigList,
        areaConfigList,
        statusGroupList,
    } = useAptConfig({
        inspectionTypeList: true,
        inventoryConfigList: true,
        areaConfigList: true,
        statusGroupList: true,
        cleanUp: true,
    })

    const { workspaceUser } = useUser()

    const hasEditPermission = hasPermission(
        workspaceUser,
        INSPECTION_PREVIEW_EDITOR,
    )

    // Find the inspection type if id is provided
    const inspectionType = id
        ? inspectionTypeList?.find((it) => it.id === Number(id))
        : undefined

    const [tab, setTab] = useState(() => {
        if (initialTabParam === 'preview') {
            return PREVIEW_INSPECTION_TYPE_STEP
        }
        return CONGIFURE_INSPECTION_TYPE_STEP
    })
    const [inspectionTypeName, setInspectionTypeName] = useState('')
    const [
        inspectionTypeCategory,
        setInspectionTypeCategory,
    ] = useState<InspectionTypeCategory>('other')
    const [areaOrder, setAreaOrder] = useState<AreaOrder>(
        AreaOrder.ALPHABETICAL,
    )
    const [invItemsMap, setInvItemsMap] = useState<IdBoolMap>({})
    const [inventoryOrder, setInventoryOrder] = useState<number[]>([])

    const [moveOutInspection, setMoveOutInspection] = useState<
        number | undefined
    >()

    useEffect(() => {
        dispatch(getStatusGroupList({}))
        dispatch(getStatusList({}))
    }, [])

    useEffect(() => {
        if (workspaceUser?.active_workspace.move_out_inspection_config) {
            setMoveOutInspection(
                workspaceUser.active_workspace.move_out_inspection_config,
            )
        }
    }, [workspaceUser])

    useEffect(() => {
        if (inspectionType) {
            setInspectionTypeName(inspectionType.name)
            setInspectionTypeCategory(inspectionType.category)
            setAreaOrder(inspectionType.area_order ?? AreaOrder.ALPHABETICAL)
        } else {
            setInspectionTypeName('')
            setAreaOrder(AreaOrder.ALPHABETICAL)
        }

        const newCheckedMap: IdBoolMap =
            inventoryConfigList?.reduce<IdBoolMap>((prev, iC, idx) => {
                return { ...prev, [iC.id]: false }
            }, {}) ?? {}
        if (inspectionType) {
            inspectionType.inventory_configs.forEach((iC) => {
                newCheckedMap[iC.id] = true
            })
            // Initialize inventory order from inspection type
            setInventoryOrder(
                inspectionType.inventory_configs.map((iC) => iC.id),
            )
        } else {
            setInventoryOrder([])
        }
        setInvItemsMap(newCheckedMap)
    }, [inspectionType, inventoryConfigList])

    const handleClose = () => {
        history.push(`${Routes.apartment.inspection.root}#types`)
    }

    const saveInspectionType = () => {
        let invItems: number[] = []

        // Use custom order if available, otherwise use default order
        if (inventoryOrder.length > 0) {
            // Filter to only include items that are still selected
            invItems = inventoryOrder.filter((id) => invItemsMap[id])
            // Add any newly selected items that aren't in the order
            inventoryConfigList?.forEach((invConfig) => {
                if (
                    invItemsMap[invConfig.id] &&
                    !invItems.includes(invConfig.id)
                ) {
                    invItems.push(invConfig.id)
                }
            })
        } else {
            // Default order: iterate through inventoryConfigList
            inventoryConfigList?.forEach((invConfig) => {
                if (invItemsMap[invConfig.id]) {
                    invItems.push(invConfig.id)
                }
            })
        }

        if (inspectionType) {
            const req: EditInspectionTypeRequest = {
                id: inspectionType.id,
                body: {
                    name: inspectionTypeName,
                    category: inspectionTypeCategory,
                    inv_configs: invItems,
                    area_order: areaOrder,
                },
            }

            dispatch(editInspectionTypeReq(req)).then(() => {
                toast.success(`${inspectionTypeName} updated.`)
                handleClose()
            })
        } else {
            const req: CreateInspectionTypeRequest = {
                body: {
                    name: inspectionTypeName,
                    category: inspectionTypeCategory,
                    inv_configs: invItems,
                    area_order: areaOrder,
                },
            }

            dispatch(createInspectionType(req)).then(() => {
                toast.success(`${inspectionTypeName} created.`)
                handleClose()
            })
        }
    }

    const SubHeaderCellStyle: React.CSSProperties = {
        fontWeight: 500,
        fontSize: '15px',
        marginBottom: theme.spacing(2),
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                height: 'calc(100vh - 104px)',
                width: '100%',
                backgroundColor: theme.palette.background.default,
                overflow: 'hidden',
            }}
        >
            <Paper
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    borderRadius: '20px',
                    width: '100%',
                    height: '100%',
                    padding: theme.spacing(1, 4, 1, 4),
                    overflow: 'hidden',
                }}
            >
                <Container
                    style={{
                        padding: theme.spacing(1, 0, 4, 0),
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                    direction="column"
                >
                    {/* Step 1 */}
                    {tab === CONGIFURE_INSPECTION_TYPE_STEP && (
                        <ConfigureInspectionType
                            inspectionTypeName={inspectionTypeName}
                            invItemsMap={invItemsMap}
                            SubHeaderStyle={SubHeaderCellStyle}
                            inspectionType={inspectionType}
                            areaConfigList={areaConfigList ?? []}
                            inspectionTypeCategory={inspectionTypeCategory}
                            setInspectionTypeCategory={
                                setInspectionTypeCategory
                            }
                            areaOrder={areaOrder}
                            setAreaOrder={setAreaOrder}
                            setInspectionTypeName={setInspectionTypeName}
                            handleClose={handleClose}
                            setTab={setTab}
                            setInvItemsMap={setInvItemsMap}
                            setMoveOutInspection={setMoveOutInspection}
                        />
                    )}

                    {/* Step 2 */}
                    {tab === PREVIEW_INSPECTION_TYPE_STEP && (
                        <Slide
                            direction="left"
                            in={tab === PREVIEW_INSPECTION_TYPE_STEP}
                        >
                            <Paper
                                elevation={0}
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <PreviewInspectionType
                                    invItemsMap={invItemsMap}
                                    inspectionType={inspectionType}
                                    inventoryConfigList={
                                        inventoryConfigList ?? []
                                    }
                                    areaConfigList={areaConfigList ?? []}
                                    statusGroupList={statusGroupList ?? []}
                                    hasEditPermission={hasEditPermission}
                                    areaOrder={areaOrder}
                                    setInvItemsMap={setInvItemsMap}
                                    inventoryOrder={inventoryOrder}
                                    setInventoryOrder={setInventoryOrder}
                                    setTab={setTab}
                                    handleClose={handleClose}
                                    saveInspectionType={saveInspectionType}
                                />
                            </Paper>
                        </Slide>
                    )}
                </Container>
            </Paper>
        </Container>
    )
}
